/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import { useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import Rating from '@mui/material/Rating';
import TextareaAutosize from '@mui/material/TextareaAutosize';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import LoadingButton from '@mui/lab/LoadingButton';

import { useAppDispatch, useAppSelector } from '../../../../app/hooks';
import {
  submitMedPearlOneFeedbackThunk,
  selectFeedbackInfo,
  selectIsSubmitting,
  setHelpfulArea,
  setHelpfulScore,
  setHowCanImprove,
} from './medPearlOneFeedbackSlice';
import styles from './MedPearlOneFeedbackForm.module.scss';

const AREAS = [
  'Management & Work-Up',
  'Referral Not Needed',
  'Referral Navigation (helped with specialist selection and/or referral urgency)',
  'Validated My Plan',
];

const labels: { [index: string]: string } = {
  1: 'Useless',
  2: 'Poor',
  3: 'Ok',
  4: 'Good',
  5: 'Excellent',
};
function getLabelText(value: number) {
  return `${value} Star${value !== 1 ? 's' : ''}, ${labels[value]}`;
}
export default function MedPearlOneFeedbackForm({ open, onClose }: { open: boolean; onClose: () => void }) {
  const formData = useAppSelector(selectFeedbackInfo);
  const { helpful_in_areas: areas, helpful_score: rating, how_can_improve: idea } = formData;
  const submitting = useAppSelector(selectIsSubmitting);
  const [hover, setHover] = useState<number>(-1);
  const dispatch = useAppDispatch();

  const handleHelpfulAreaChange = (option: string) => {
    const index = areas.indexOf(option);
    if (index === -1) {
      dispatch(setHelpfulArea([...areas, option]));
    } else {
      const selectedAreas = [...areas];
      selectedAreas.splice(index, 1);
      dispatch(setHelpfulArea(selectedAreas));
    }
  };

  const submit = async () => {
    const data = await dispatch(submitMedPearlOneFeedbackThunk(formData));
    if (data.payload) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Guide and Algorithm Feedback Form</DialogTitle>
      <DialogContent>
        <p className={styles.highlightQuestion}>
          The Referral Guide or Algorithm was helpful in the following area(s), check all that apply:
        </p>
        <FormGroup className={styles.areas}>
          {AREAS.map((area) => (
            <FormControlLabel
              checked={areas.indexOf(area) !== -1}
              disabled={submitting}
              control={<Checkbox onChange={() => handleHelpfulAreaChange(area)} color="green" />}
              key={area}
              label={area}
            />
          ))}
        </FormGroup>
        <Divider className={styles.divider} />
        <p>How helpful was this Referral Guide or Algorithm?</p>
        <Box className={styles.ratingBox}>
          <Rating
            name="rating"
            size="large"
            value={rating}
            onChange={(_, newValue) => {
              dispatch(setHelpfulScore(newValue));
            }}
            onChangeActive={(_, newHover) => {
              setHover(newHover);
            }}
            getLabelText={getLabelText}
            disabled={submitting}
          />
          <Box sx={{ ml: 2, alignSelf: 'center' }}>{labels[hover !== -1 ? hover : rating || 0]}</Box>
        </Box>
        <Divider className={styles.divider} />
        <p>How can we improve this Referral Guide or Algorithm?</p>
        <TextareaAutosize
          className={styles.ideaInput}
          name="idea"
          placeholder="Enter text here…"
          value={idea}
          onChange={(e) => dispatch(setHowCanImprove(e.target.value || ''))}
          minRows={6}
          disabled={submitting}
        />
      </DialogContent>
      <DialogActions>
        <LoadingButton
          loading={submitting}
          id="submit-user-feedback"
          variant="contained"
          color="green"
          onClick={submit}
          disabled={submitting}
        >
          Submit feedback
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
